import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { getAuthToken } from '../../utils/authUtils';
import { withRequestDedupe, SINGLETON_KEY } from '../requestDedupe';

const initialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  userDetails: null,
  updateUserDetails: {
    processing: null,
    done: null,
    failed: null,
    error: null,
    errorText: null,
  },
};

export const fetchUserDetails = createAsyncThunk(
  'userDetails/fetchUserDetails',
  // Argument-less and dispatched from 77 sites, several of which fire in the
  // same tick on a cold load. Concurrent dispatches share one request.
  //
  // `api.signal` is deliberately NOT forwarded on deduped thunks: the request
  // is shared, so aborting one dispatch would abort it for every other
  // dispatch waiting on the same promise.
  withRequestDedupe(SINGLETON_KEY, async (_, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue('User not authenticated');

    const userInfo = localStorage.getItem('userInfo');
    const userId = userInfo ? JSON.parse(userInfo)?.id : null;
    if (!userId) return rejectWithValue('User ID not found in local storage');

    try {
      // The Authorization header is attached by the axios request interceptor.
      const response = await axios.get(`/us/users/${userId}`, {
        params: { 'request-id': uuidv4() },
      });
      return response.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data || e.message || 'Failed to fetch user details',
      );
    }
  }),
);

export const updateUserDetails = createAsyncThunk(
  'userDetails/updateUserDetails',
  async (userRequest, { rejectWithValue, signal }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue('User not authenticated');

    const userInfo = localStorage.getItem('userInfo');
    const userId = JSON.parse(userInfo || '{}')?.id;

    try {
      const response = await axios.put(`/us/users/${userId}`, userRequest, {
        params: { 'request-id': uuidv4() },
        signal,
      });
      return response.data;
    } catch (e) {
      return rejectWithValue(
        e?.response?.data || e.message || 'Failed to update user details',
      );
    }
  },
);

const userDetailsSlice = createSlice({
  name: 'userDetails',
  initialState,
  reducers: {
    // Standard reducers if needed
    setUserDetails: (state, action) => {
      state.userDetails = action.payload;
    },
    clearUserDetails: (state) => {
      state.userDetails = null;
      state.fetching = false;
      state.fetchingSuccessful = false;
      state.fetchingFailed = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.fetching = true;
        state.fetchingSuccessful = false;
        state.fetchingFailed = false;
        state.error = false;
        state.errorText = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.fetching = false;
        state.fetchingSuccessful = true;
        state.fetchingFailed = false;
        state.error = false;
        state.errorText = '';
        state.userDetails = action.payload;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.fetching = false;
        state.fetchingFailed = true;
        state.error = true;
        state.errorText = action.payload || 'Failed to fetch user details';
        state.userDetails = null;
      });

    builder
      .addCase(updateUserDetails.pending, (state) => {
        state.updateUserDetails.processing = true;
        state.updateUserDetails.done = false;
        state.updateUserDetails.failed = false;
        state.updateUserDetails.error = null;
        state.updateUserDetails.errorText = null;
      })
      .addCase(updateUserDetails.fulfilled, (state) => {
        state.updateUserDetails.processing = false;
        state.updateUserDetails.done = true;
        state.updateUserDetails.failed = false;
        state.updateUserDetails.error = null;
        state.updateUserDetails.errorText = null;
      })
      .addCase(updateUserDetails.rejected, (state, action) => {
        state.updateUserDetails.processing = false;
        state.updateUserDetails.done = false;
        state.updateUserDetails.failed = true;
        state.updateUserDetails.error = true;
        state.updateUserDetails.errorText = action.payload;
      });
  },
});

export const { clearUserDetails, setUserDetails } = userDetailsSlice.actions;
export const updateUserDetailsThunk = updateUserDetails;
export default userDetailsSlice.reducer;
